import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mobileMediaQuery, themeColor } from '../../Theming';
import CountUp from 'react-countup';
import cloud from '../../Assets/TreePlanting/cloudBlack.png';
import car from '../../Assets/TreePlanting/carBlack.png';
import { Maybe } from '../TreeplantingContext';

const Container = styled.section`
  margin: 72px 0 0 0;

  ${mobileMediaQuery} {
    margin: 32px 0 0 0;
  }
`;

const Header = styled.h2`
  margin: 0 0 32px 0;
  padding: 0;
  font-weight: normal;
  font-family: SuisseIntl-Medium;
  text-align: center;
  font-size: 32px;

  ${mobileMediaQuery} {
    margin: 0 0 8px 0;
    font-size: 16px;
    line-height: 20px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  text-align: center;
  justify-content: space-evenly;
  align-items: center;
`;

const InnerContentContainer = styled.div`
  width: 100%;
  height: 277px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  background-color: ${themeColor('greyBackground')};
  margin-right: 24px;
  border-radius: 20px;

  ${mobileMediaQuery} {
    margin-right: 8px;
  }

  &:last-child {
    margin-right: 0;
  }

  [dir='rtl'] & {
    &:first-child {
      margin-right: 0;
    }

    &:last-child {
      margin-right: 24px;

      ${mobileMediaQuery} {
        margin-right: 8px;
      }
    }
  }

  ${mobileMediaQuery} {
    border-radius: 7px;
    height: 93px;
  }
`;

const IconContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  width: 160px;
  height: 108px;
  margin: 20px 0 20px 0;
  ${mobileMediaQuery} {
    width: 37px;
    height: 25px;
    margin: 10px 0 15px 0;
  }
`;

const Description = styled.p`
  font-size: 23px;
  line-height: 25px;
  color: ${themeColor('greyText')};
  margin-top: 0px;
  font-family: SuisseIntl-Medium;

  ${mobileMediaQuery} {
    font-size: 12px;
    line-height: 5px;
  }
`;

const Value = styled.p`
  font-weight: normal;
  font-size: 34px;
  line-height: 24px;
  margin: 0 0 15px 0;
  color: ${themeColor('darkestGreen')};
  font-family: SuisseIntl-Bold;

  ${mobileMediaQuery} {
    font-size: 16px;
    line-height: 0px;
  }
`;

interface Props {
  tonnesOfCo2?: Maybe<number>;
  milesDriven?: Maybe<number>;
}

const EquivalentSection: React.FC<Props> = ({ tonnesOfCo2, milesDriven }) => {
  const { t } = useTranslation();

  /*if whole number don't add any decimal places */
  const getDecimal = (value: number) => (Number.isInteger(value) ? 0 : 2);
  return (
    <Container>
      <Header>{t('treePlantingV2.equivalent.heading')}</Header>
      <ContentContainer>
        <InnerContentContainer>
          <IconContainer>
            <Icon src={cloud} />
          </IconContainer>

          <Value>
            <CountUp
              decimals={getDecimal(tonnesOfCo2 as number)}
              start={0}
              end={tonnesOfCo2 as number}
              duration={1}
            />
          </Value>
          <Description>{t('treePlantingV2.equivalent.co2')}</Description>
        </InnerContentContainer>

        <InnerContentContainer>
          <IconContainer>
            <Icon src={car} />
          </IconContainer>

          <Value>
            <CountUp
              decimals={getDecimal(milesDriven as number)}
              start={0}
              end={milesDriven as number}
              duration={1}
            />
          </Value>
          <Description>{t('treePlantingV2.equivalent.miles')}</Description>
        </InnerContentContainer>
      </ContentContainer>
    </Container>
  );
};

export default EquivalentSection;
